/* assets/gproperty-flow-context.js — GPROPERTY-DRAFT-CONTRACT-02
 * Canonical browser-only contract owner for public readiness and real location provenance,
 * exact fail-closed recognition, v2-first/v1-fallback non-contact local drafts,
 * and strict record_id/result_id separation.
 * Never calls deep status or family-data ingest.
 */
(function (window) {
  'use strict';

  var VERSION = 'gproperty-draft-contract-02';
  var DEFAULT_TIMEOUT_MS = 12000;
  var CANONICAL_DRAFT_KEY = 'gproperty_spatial_brief_draft_v2';
  var LEGACY_DRAFT_KEY = 'gproperty_spatial_brief_draft_v1';
  var LOCATION_PROVENANCE_CONTRACT = 'gproperty_real_location_provenance_v1';

  if (window.GPROPERTY_DATA_GATE && window.GPROPERTY_DATA_GATE.version === VERSION) {
    return;
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 1. SANITIZERS & STORAGE HELPERS
   * ────────────────────────────────────────────────────────────────────────── */
  function clean(value) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim();
  }

  function opaque(value) {
    var text = clean(value);
    return /^[A-Za-z0-9._:-]{1,160}$/.test(text) ? text : '';
  }

  function finite(value) {
    if (value === '' || value == null) return null;
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function hash(value) {
    var text = clean(value);
    var result = 2166136261;

    for (var index = 0; index < text.length; index += 1) {
      result ^= text.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }

    return (result >>> 0).toString(36);
  }

  function read(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch (_) {
      return null;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  }

  function copyObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }
    return Object.assign({}, value);
  }

  function safeArray(value) {
    return Array.isArray(value) ? value.slice(0, 40) : [];
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 2. ESTATE CONTEXT NORMALIZATION & IDENTIFIERS
   * ────────────────────────────────────────────────────────────────────────── */
  function normalizeEstateContext(value) {
    var estate = copyObject(value);

    return {
      verification_state: clean(estate.verification_state) || 'unverified_local',
      provenance_contract: clean(estate.provenance_contract),
      source: clean(estate.source),
      source_provider: clean(estate.source_provider),
      source_dataset: clean(estate.source_dataset),
      source_update_frequency: clean(estate.source_update_frequency),
      source_ref: opaque(estate.source_ref),
      lookup_request_id: opaque(estate.lookup_request_id),
      lookup_result_id: opaque(estate.lookup_result_id),
      rank: finite(estate.rank),
      match_score: finite(estate.match_score),
      estate_id: opaque(estate.estate_id),
      name_zh: clean(estate.name_zh || estate.name),
      name_en: clean(estate.name_en),
      address_zh: clean(estate.address_zh || estate.formatted_address_zh || estate.address),
      address_en: clean(estate.address_en || estate.formatted_address_en),
      district_code: opaque(estate.district_code),
      district_zh: clean(estate.district_zh || estate.district),
      district_en: clean(estate.district_en),
      region_zh: clean(estate.region_zh),
      region_en: clean(estate.region_en),
      lat: finite(estate.lat),
      lng: finite(estate.lng),
      coordinate_verified: estate.coordinate_verified === true,
      geo_address: clean(estate.geo_address),
      query: clean(estate.query),
      retrieved_at: clean(estate.retrieved_at),
      mock_data_used: estate.mock_data_used === true
    };
  }

  function locationLabel(context) {
    if (!context || typeof context !== 'object') {
      return '';
    }

    return clean(
      context.name_zh ||
      context.name_en ||
      context.address_zh ||
      context.address_en ||
      context.geo_address
    );
  }

  function isVerifiedEstate(context) {
    var provider = clean(context && context.source_provider).toLowerCase();
    return !!(
      context &&
      context.verification_state === 'verified' &&
      context.provenance_contract === LOCATION_PROVENANCE_CONTRACT &&
      context.source === 'hk_dpo_als' &&
      provider === 'digital policy office' &&
      context.coordinate_verified === true &&
      opaque(context.source_ref) &&
      context.mock_data_used === false &&
      locationLabel(context)
    );
  }

  function locationSourceMeta(sourceId) {
    var id = clean(sourceId);
    if (id === 'hk_dpo_als') {
      return { provider: 'Digital Policy Office', dataset: 'Address Lookup Service', verification_state: 'verified' };
    }
    if (id === 'hk_landsd_location_search') {
      return { provider: 'Lands Department', dataset: 'Location Search API', verification_state: 'resolved_fallback' };
    }
    if (id === 'osm_nominatim') {
      return { provider: 'OpenStreetMap Nominatim', dataset: 'Nominatim Search', verification_state: 'resolved_fallback' };
    }
    return { provider: '', dataset: '', verification_state: 'resolved_fallback' };
  }

  function estateIdentity(context) {
    var estate = normalizeEstateContext(context);

    return JSON.stringify([
      estate.source,
      opaque(estate.source_ref),
      opaque(estate.estate_id),
      opaque(estate.lookup_result_id),
      locationLabel(estate),
      estate.address_zh,
      estate.address_en,
      estate.district_code,
      estate.district_zh,
      estate.district_en,
      estate.lat,
      estate.lng
    ]);
  }

  function generateRecordId(seed) {
    return [
      'gproperty',
      Date.now(),
      hash(seed || String(Date.now()))
    ].join('-');
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 3. PROPERTY DRAFT MANAGEMENT
   * ────────────────────────────────────────────────────────────────────────── */
  function normalizePropertyDraft(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    var now = new Date().toISOString();
    var rawEstate = copyObject(value.estate_context);

    if (!rawEstate.estate_id && value.estate_id) {
      rawEstate.estate_id = value.estate_id;
    }

    if (!rawEstate.name_zh && !rawEstate.name && value.project_location) {
      rawEstate.name = value.project_location;
    }

    if (!rawEstate.address && value.address) {
      rawEstate.address = value.address;
    }

    if (!rawEstate.district && value.district) {
      rawEstate.district = value.district;
    }

    var estate = normalizeEstateContext(rawEstate);
    var location = clean(value.project_location) || locationLabel(estate) || clean(estate.query);
    var directRecordId = opaque(value.record_id);

    var recordId = directRecordId || generateRecordId([
      opaque(estate.estate_id),
      opaque(estate.lookup_result_id),
      location,
      clean(value.created_at)
    ].join('|'));

    var resultId = opaque(value.result_id);

    /* Historical drafts wrote record_id === result_id. Clear contaminated result_id. */
    if (resultId === recordId) {
      resultId = '';
    }

    return {
      version: 2,
      record_id: recordId,
      result_id: resultId,
      source: clean(value.source) || 'gproperty',
      intent: clean(value.intent) || 'property-spatial-brief',
      entry_source: clean(value.entry_source),
      entry_intent: clean(value.entry_intent),
      source_card_type: clean(value.source_card_type) || 'gproperty_spatial_brief',
      project_location: location,
      estate_context: estate,
      asset_type: clean(value.asset_type),
      business_need: clean(value.business_need),
      project_stage: clean(value.project_stage),
      description: clean(value.description),
      evidence_files: safeArray(value.evidence_files),
      fit_references: copyObject(value.fit_references),
      formal_submission_status: clean(value.formal_submission_status) || 'not_submitted',
      review_brief_status: clean(value.review_brief_status) || (
        isVerifiedEstate(estate)
          ? 'verified_local_saved'
          : 'unverified_local_saved'
      ),
      backend_submission: value.backend_submission === true,
      family_analysis_requested: value.family_analysis_requested === true,
      owner_analysis_requested: value.owner_analysis_requested === true,
      school_analysis_requested: value.school_analysis_requested === true,
      valuation_conclusion: value.valuation_conclusion === true,
      surveying_conclusion: value.surveying_conclusion === true,
      created_at: clean(value.created_at) || now,
      updated_at: clean(value.updated_at) || now
    };
  }

  function readPropertyDraft(primaryKey, legacyKey) {
    var canonicalKey = clean(primaryKey) || CANONICAL_DRAFT_KEY;
    var oldKey = clean(legacyKey) || LEGACY_DRAFT_KEY;

    var canonical = normalizePropertyDraft(read(canonicalKey));

    if (canonical) {
      return {
        draft: canonical,
        source_key: canonicalKey,
        migrated: false
      };
    }

    var legacy = normalizePropertyDraft(read(oldKey));

    if (!legacy) {
      return {
        draft: null,
        source_key: '',
        migrated: false
      };
    }

    legacy.version = 2;
    legacy.updated_at = new Date().toISOString();

    return {
      draft: legacy,
      source_key: oldKey,
      migrated: write(canonicalKey, legacy)
    };
  }

  function writePropertyDraft(primaryKey, value) {
    var canonicalKey = clean(primaryKey) || CANONICAL_DRAFT_KEY;
    var normalized = normalizePropertyDraft(value);

    return normalized ? write(canonicalKey, normalized) : false;
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 4. NETWORK TRANSPORT & FAIL-CLOSED GATEWAY READINESS
   * ────────────────────────────────────────────────────────────────────────── */
  function request(url, options, timeoutMs) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;

    var timeout = window.setTimeout(function () {
      if (controller) {
        controller.abort();
      }
    }, timeoutMs || DEFAULT_TIMEOUT_MS);

    var requestOptions = Object.assign({
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    }, options || {});

    if (controller) {
      requestOptions.signal = controller.signal;
    }

    return window.fetch(url, requestOptions)
      .then(function (response) {
        return response.json().catch(function () {
          return {};
        }).then(function (body) {
          return {
            http_status: response.status,
            response_ok: response.ok,
            body: body && typeof body === 'object' ? body : {}
          };
        });
      })
      .finally(function () {
        window.clearTimeout(timeout);
      });
  }

  function publicReadiness(endpoint) {
    var safe = clean(endpoint);

    if (!safe || safe.indexOf('deep=1') > -1 || safe.indexOf('/family-data/ingest') > -1) {
      return Promise.reject(new Error('unsafe_readiness_endpoint'));
    }

    var url;

    try {
      url = new URL(safe, window.location.origin);
    } catch (_) {
      return Promise.reject(new Error('readiness_endpoint_invalid'));
    }

    if (url.origin !== window.location.origin) {
      return Promise.reject(new Error('cross_origin_readiness_forbidden'));
    }

    return request(url.pathname + url.search, { method: 'GET' });
  }

  function isPublicRedacted(body) {
    var runtime = body && body.runtime && typeof body.runtime === 'object' ? body.runtime : {};

    return !!(
      body &&
      body.ok === true &&
      body.success === true &&
      body.status_mode === 'public_redacted' &&
      body.family_data_ready === false &&
      body.mock_data_used !== true &&
      runtime.runtime_ready === true &&
      runtime.serving_ready === false
    );
  }

  function isServingReady(body) {
    var runtime = body && body.runtime && typeof body.runtime === 'object' ? body.runtime : {};

    return !!(
      body &&
      body.ok === true &&
      body.success === true &&
      body.status_mode === 'public_redacted' &&
      body.family_data_ready === true &&
      body.mock_data_used !== true &&
      runtime.runtime_ready === true &&
      runtime.serving_ready === true
    );
  }

  function isFailClosed(httpStatus, body) {
    return !!(
      httpStatus === 503 &&
      body &&
      body.ok === false &&
      body.success === false &&
      body.status === 'data_source_not_ready' &&
      body.error === 'verified_family_data_unavailable' &&
      body.mock_data_used === false &&
      body.is_publishable === false &&
      Array.isArray(body.items) && body.items.length === 0 &&
      Array.isArray(body.results) && body.results.length === 0 &&
      Array.isArray(body.recommendations) && body.recommendations.length === 0
    );
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 5. REAL LOCATION LOOKUP PROVENANCE & DRAFT CREATION
   * ────────────────────────────────────────────────────────────────────────── */
  function searchLocations(endpoint, query) {
    var safe = clean(endpoint);
    var term = clean(query);

    if (!safe) {
      return Promise.reject(new Error('location_search_endpoint_missing'));
    }

    if (safe.indexOf('/family-data/ingest') > -1 || safe.indexOf('deep=1') > -1) {
      return Promise.reject(new Error('unsafe_location_search_endpoint'));
    }

    if (term.length < 2) {
      return Promise.reject(new Error('query_too_short'));
    }

    if (term.length > 255) {
      return Promise.reject(new Error('query_too_long'));
    }

    var url;

    try {
      url = new URL(safe, window.location.origin);
    } catch (_) {
      return Promise.reject(new Error('location_search_endpoint_invalid'));
    }

    if (url.origin !== window.location.origin) {
      return Promise.reject(new Error('cross_origin_location_search_forbidden'));
    }

    url.searchParams.set('q', term);

    return request(url.pathname + url.search, { method: 'GET' }, 10000);
  }

  function normalizeLocation(item, responseBody) {
    var responseSource = responseBody && responseBody.source && typeof responseBody.source === 'object'
      ? responseBody.source
      : {};
    var sourceId = clean(item && item.source);
    var meta = locationSourceMeta(sourceId);
    var provider = clean(item && item.source_provider) || meta.provider;
    var dataset = clean(item && item.source_dataset) || meta.dataset;
    var mockUsed = item && item.mock_data_used === true || responseBody && responseBody.mock_data_used === true;
    var officialAls = !!(
      sourceId === 'hk_dpo_als' &&
      provider.toLowerCase() === 'digital policy office' &&
      item && item.coordinate_verified === true &&
      opaque(item.source_ref) &&
      mockUsed === false
    );

    return normalizeEstateContext({
      verification_state: officialAls ? 'verified' : 'resolved_fallback',
      provenance_contract: LOCATION_PROVENANCE_CONTRACT,
      source: sourceId,
      source_provider: provider,
      source_dataset: dataset,
      source_update_frequency: clean(item && item.source_update_frequency) || clean(responseSource.update_frequency),
      source_ref: item && item.source_ref,
      lookup_request_id: responseBody && responseBody.request_id,
      lookup_result_id: item && (item.result_id || item.place_id) || responseBody && responseBody.result_id,
      rank: item && item.rank,
      match_score: item && (item.match_score == null ? item.confidence : item.match_score),
      estate_id: item && (item.estate_id || item.place_id),
      name_zh: item && item.name_zh,
      name_en: item && item.name_en,
      address_zh: item && item.formatted_address_zh,
      address_en: item && item.formatted_address_en,
      district_code: item && item.district_code,
      district_zh: item && item.district_zh,
      district_en: item && item.district_en,
      region_zh: item && item.region_zh,
      region_en: item && item.region_en,
      lat: item && item.lat,
      lng: item && item.lng,
      coordinate_verified: item && item.coordinate_verified === true,
      geo_address: item && item.geo_address,
      retrieved_at: clean(item && item.retrieved_at) || clean(responseSource.retrieved_at),
      mock_data_used: mockUsed
    });
  }

  function isVerifiedDraft(draft) {
    var normalized = normalizePropertyDraft(draft);

    return !!(
      normalized &&
      normalized.source === 'gproperty' &&
      isVerifiedEstate(normalized.estate_context)
    );
  }

  function createPropertyDraft(query, estateContext, existing, routeContext) {
    var record = normalizePropertyDraft(existing) || {};
    var route = copyObject(routeContext);
    var normalizedEstate = normalizeEstateContext(estateContext);
    var verified = isVerifiedEstate(normalizedEstate);
    var location = verified ? locationLabel(normalizedEstate) : clean(query);
    var now = new Date().toISOString();

    var recordId = opaque(record.record_id) || generateRecordId([
      opaque(normalizedEstate.estate_id),
      opaque(normalizedEstate.lookup_result_id),
      location,
      verified ? 'verified' : 'unverified'
    ].join('|'));

    var previousEstateIdentity = estateIdentity(record.estate_context);
    var nextEstateIdentity = estateIdentity(normalizedEstate);
    var estateChanged = !!((previousEstateIdentity || nextEstateIdentity) && previousEstateIdentity !== nextEstateIdentity);

    var resultId = estateChanged ? '' : opaque(record.result_id);

    if (resultId === recordId) {
      resultId = '';
    }

    return normalizePropertyDraft(Object.assign({}, record, {
      version: 2,
      record_id: recordId,
      result_id: resultId,
      source: 'gproperty',
      intent: 'property-spatial-brief',
      entry_source: clean(route.source),
      entry_intent: clean(route.intent),
      source_card_type: 'gproperty_spatial_brief',
      project_location: location,
      estate_context: verified ? normalizedEstate : {
        verification_state: 'unverified_local',
        query: clean(query),
        mock_data_used: false
      },
      fit_references: estateChanged ? {} : copyObject(record.fit_references),
      formal_submission_status: 'not_submitted',
      review_brief_status: verified ? 'verified_local_saved' : 'unverified_local_saved',
      backend_submission: false,
      family_analysis_requested: false,
      owner_analysis_requested: false,
      school_analysis_requested: false,
      valuation_conclusion: false,
      surveying_conclusion: false,
      created_at: clean(record.created_at) || now,
      updated_at: now
    }));
  }

  function safeError(error) {
    if (error && error.name === 'AbortError') {
      return 'request_timeout';
    }
    return clean(error && error.message) || 'request_failed';
  }

  /* ──────────────────────────────────────────────────────────────────────────
   * 6. EXPORT IMMUTABLE GLOBAL DATA GATE API
   * ────────────────────────────────────────────────────────────────────────── */
  window.GPROPERTY_DATA_GATE = Object.freeze({
    version: VERSION,
    locationProvenanceContract: LOCATION_PROVENANCE_CONTRACT,
    canonicalDraftKey: CANONICAL_DRAFT_KEY,
    legacyDraftKey: LEGACY_DRAFT_KEY,
    clean: clean,
    opaque: opaque,
    finite: finite,
    hash: hash,
    read: read,
    write: write,
    normalizeEstateContext: normalizeEstateContext,
    estateIdentity: estateIdentity,
    normalizePropertyDraft: normalizePropertyDraft,
    readPropertyDraft: readPropertyDraft,
    writePropertyDraft: writePropertyDraft,
    request: request,
    publicReadiness: publicReadiness,
    isPublicRedacted: isPublicRedacted,
    isServingReady: isServingReady,
    isFailClosed: isFailClosed,
    searchLocations: searchLocations,
    normalizeLocation: normalizeLocation,
    isVerifiedEstate: isVerifiedEstate,
    locationSourceMeta: locationSourceMeta,
    locationLabel: locationLabel,
    isVerifiedDraft: isVerifiedDraft,
    createPropertyDraft: createPropertyDraft,
    safeError: safeError
  });
})(window);
